import { useEffect, useState } from 'react';

import styled, { keyframes } from 'styled-components';
import moment from 'moment';

import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';
import { useLanguage } from '../../../Context/LanguageContext';

// 기간별 매출 표시 전용 뷰 (집계 완료된 값만 받아 렌더) //
// - 통계: SalesPeriodBox (원본 → 집계, 정산 포함) //
// - AI 채팅: SalesPeriodRenderer (매출 버퍼 집계, 정산 숨김) //
export default function SalesPeriodView(props) {
  const {
    startDate,               // moment 또는 ISO — 타이틀용
    endDate,
    monthData,               // [{ date(moment/ISO), salesPrice }] (금액 내림차순)
    periodSalesPrice,
    settlementPrice,
    expectedSettlementPrice,
    isOpenPartnerGymSales = true,
    showSettlement = true,   // 정산 2박스 노출 여부 (AI는 merchantUid/정산소스 없어 false) //
  } = props;

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();

  const [animationKey, setAnimationKey] = useState(0);

  // 매출 순위에 따른 bar 색상 (밝음 → 어두움) //
  const barColors = [
    theme.brand500, theme.brand550, theme.brand600, theme.brand650, theme.brand700,
    theme.brand750, theme.brand800, theme.brand850, theme.brand900
  ];

  // monthData 변경 시 bar 리스트 재마운트 → grow 애니메이션 재생 //
  useEffect(() => {
    setAnimationKey(prev => prev + 1);
  }, [monthData]);

  // 기간 타이틀 텍스트 정리 //
  function decidePeriodTitle() {
    const format = t('statistics.periodSales.monthTitleFormat');

    return moment(startDate).format(format) + ' - ' + moment(endDate).format(format);
  }

  // 매출 최댓값 대비 bar 너비 비율 //
  function decideBarWidth(salesPrice, maxPrice) {
    if (maxPrice <= 0) return 0;

    return (salesPrice / maxPrice) * 100;
  }

  // 매출 순위에 따른 bar 색상 //
  function decideBarColor(rank, total) {
    if (total <= 1) return barColors[0];

    const colorIndex = Math.round((rank / (total - 1)) * (barColors.length - 1));

    return barColors[colorIndex];
  }

  // 월별 매출 bar 리스트 렌더 //
  function renderMonthBars() {
    const rows = Array.isArray(monthData) ? monthData : [];
    const maxPrice = rows.length > 0 ? rows[0].salesPrice : 0;

    return rows.map((data, index) => (
      <MonthRow key={'period-month-' + animationKey + '-' + index}>
        <MonthLabelBox>
          <Text fontFamily='NotoSansKR-Medium' size={20} color={theme.text} cursor='default'>{moment(data.date).format(t('statistics.periodSales.monthLabelFormat'))}</Text>
        </MonthLabelBox>
        <BarTrack $isDark={isDark}>
          <BarFill $width={decideBarWidth(data.salesPrice, maxPrice)} $color={decideBarColor(index, rows.length)} />
        </BarTrack>
        <AmountBox>
          <Text fontFamily='NotoSansKR-Bold' size={20} color={theme.text} cursor='default'>{data.salesPrice.toLocaleString('ko-KR')}</Text>
          <Text fontFamily='NotoSansKR-Bold' size={14} color={theme.text} cursor='default'>{t('statistics.won')}</Text>
        </AmountBox>
      </MonthRow>
    ));
  }

  return (
    <ContentWrapper>
      <TitleWrapper>
        <Text fontFamily='NotoSansKR-Bold' size={20} color={theme.text} cursor='default'>{decidePeriodTitle()}</Text>
      </TitleWrapper>
      <InfoComponentBoxWrapper>
        <InfoComponentBox>
          <Text fontFamily='NotoSansKR-Medium' size={14} color={theme.text} cursor='default'>{t('statistics.periodSales.periodSalesAmount')}</Text>
          <ValueWrapper>
            <Text fontFamily='NotoSansKR-Bold' size={20} color={theme.text} cursor='default'>{periodSalesPrice.toLocaleString('ko-KR')}</Text>
            <Text fontFamily='NotoSansKR-Bold' size={14} color={theme.text} cursor='default'>{t('statistics.won')}</Text>
          </ValueWrapper>
        </InfoComponentBox>
        {
          showSettlement &&
          <InfoComponentBox>
            <Text fontFamily='NotoSansKR-Medium' size={14} color={theme.text} cursor='default'>{t('statistics.periodSales.periodLinkoPaySettlement')}</Text>
            <ValueWrapper>
              <Text fontFamily='NotoSansKR-Bold' size={20} color={theme.text} cursor='default'>{settlementPrice.toLocaleString('ko-KR')}</Text>
              <Text fontFamily='NotoSansKR-Bold' size={14} color={theme.text} cursor='default'>{t('statistics.won')}</Text>
            </ValueWrapper>
          </InfoComponentBox>
        }
        {
          showSettlement &&
          <InfoComponentBox>
            <Text fontFamily='NotoSansKR-Medium' size={14} color={theme.text} cursor='default'>{t('statistics.periodSales.periodLinkoPayExpectedSettlement')}</Text>
            <ValueWrapper>
              <Text fontFamily='NotoSansKR-Bold' size={20} color={theme.text} cursor='default'>{expectedSettlementPrice.toLocaleString('ko-KR')}</Text>
              <Text fontFamily='NotoSansKR-Bold' size={14} color={theme.text} cursor='default'>{t('statistics.won')}</Text>
            </ValueWrapper>
          </InfoComponentBox>
        }
      </InfoComponentBoxWrapper>
      <BarListWrapper $isScroll={(monthData?.length || 0) > 12}>
        {
          !isOpenPartnerGymSales &&
          <NotOpenOpacityBox />
        }
        {
          !isOpenPartnerGymSales &&
          <NotOpenBox>
            <Text fontFamily='NotoSansKR-Bold' size={15} color={theme.subText} cursor='default'>{t('statistics.partnerGymPrivate')}</Text>
            <Text fontFamily='NotoSansKR-Regular' size={15} color={theme.subText} cursor='default'>{t('statistics.partnerGymPrivateDesc')}</Text>
          </NotOpenBox>
        }
        {renderMonthBars()}
      </BarListWrapper>
    </ContentWrapper>
  );
}

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;

  gap: 15px;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: 100%;
`;

const InfoComponentBoxWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;

  gap: 24px;
`;

const InfoComponentBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;

  gap: 4px;
`;

const ValueWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 5px;
`;

const BarListWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  margin-top: 20px;

  width: 100%;

  gap: 15px;

  max-height: ${props => props.$isScroll ? '540px' : 'none'};
  overflow-y: ${props => props.$isScroll ? 'auto' : 'visible'};
  padding-right: ${props => props.$isScroll ? '8px' : '0px'};

  &::-webkit-scrollbar {
    width: 2px;
  }

  &::-webkit-scrollbar-track {
    background-color: ${props => props.theme.subBackground};
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 1000px;
    background-color: ${props => props.theme.brand500};
  }
`;

const MonthRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: 100%;
`;

const MonthLabelBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  flex-shrink: 0;
  width: 150px;
`;

const BarTrack = styled.div`
  position: relative;
  display: flex;

  align-items: center;

  flex: 1 0 0;
  min-width: 0px;
  height: 10px;

  border-radius: 1000px;
  background-color: ${props => props.theme.buttonFill3};
`;

const barFillGrow = keyframes`
  from {
    width: 0%;
  }
  to {
    width: var(--bar-width);
  }
`;

const BarFill = styled.div`
  position: absolute;
  left: 0;
  top: 0;

  --bar-width: ${props => props.$width}%;
  width: var(--bar-width);
  height: 10px;

  border-radius: 1000px;
  background-color: ${props => props.$color};

  animation: ${barFillGrow} 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
`;

const AmountBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-end;
  align-items: center;

  flex-shrink: 0;
  width: 200px;

  gap: 5px;
`;

const NotOpenOpacityBox = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 100%;

  opacity: 0.8;
  background-color: ${props => props.theme.background};
`;

const NotOpenBox = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 100%;
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.size}px;

  color: ${props => props.color};

  margin: 0 0 0 0;

  &:hover {
    cursor: ${props => props.cursor};
  }
`;
